/**
 * @lodex-pro/web-shared/auth/change-password — voluntary password change.
 *
 * Quick 260827-pwd. Until now a signed-in user had NO way to change their own
 * password: nothing called any change-password API, there was no forgot-password,
 * and Settings showed "Coming soon" to non-admins. The only route to a new
 * password was asking a dispatch manager to reset it and waiting for an email — so
 * a user who suspected their password was compromised could not rotate it.
 *
 * GOES THROUGH OUR API, not Amplify. The first implementation called Amplify's
 * `updatePassword` in the browser and failed on deployed dev before issuing any
 * network request: this app's session is NOT Amplify-managed. The cookie storage
 * adapter returns null for refresh-token keys because the refresh token is an
 * HttpOnly cookie (see api-client.ts), so Amplify has no usable client-side
 * session. POST /v1/auth/change-password does it server-side with the Bearer the
 * request already carries.
 *
 * Forgot-password deliberately stays on Amplify — it is PRE-auth and needs no
 * session, which is exactly why it works where this did not.
 *
 * Available to EVERY signed-in role, admin included. It grants no new capability:
 * the endpoint requires the current password and acts only on the caller's own
 * account (Cognito's ChangePassword is authorized by their access token, so it
 * structurally cannot touch anyone else). Excluding platform admins would mean an
 * admin who suspects compromise has to ask someone else to rotate it, which is
 * worse security. Forgot-password is a separate decision and stays admin-excluded.
 */

import { useState } from 'react';
import type { FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../api/api-error.js';
import { useApiOpts } from '../api/api-opts-context.js';
import { useApiMutation } from '../api/use-api-mutation.js';
import { PasswordField } from './password-field.js';
import { newPasswordPairSchema, PASSWORD_RULES_TEXT } from './password-rules.js';

const schema = z
  .object({ currentPassword: z.string().min(1, 'Enter your current password.') })
  .and(newPasswordPairSchema);

type ChangePasswordForm = z.infer<typeof schema>;

interface ChangePasswordBody {
  readonly currentPassword: string;
  readonly newPassword: string;
}

export interface ChangePasswordProps {
  /** Fired after the API confirms the change. */
  readonly onChanged?: () => void;
}

export const ChangePassword: FC<ChangePasswordProps> = ({ onChanged }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Bumped on success to remount the fields. This form stays on screen after a change,
  // and each PasswordField keeps its own show/hide state: without a remount, a field
  // left revealed would show the NEXT password typed into it in plain text (86bbnwm9d).
  const [fieldsGeneration, setFieldsGeneration] = useState(0);
  const apiOpts = useApiOpts();
  const form = useForm<ChangePasswordForm>({ resolver: zodResolver(schema) });

  const mutation = useApiMutation<ChangePasswordBody, { changed: boolean }>(
    apiOpts,
    '/v1/auth/change-password',
    'POST',
    {
      onSuccess: () => {
        form.reset();
        setFieldsGeneration((g) => g + 1);
        setDone(true);
        onChanged?.();
      },
      onError: (err: unknown) => {
        // Branch on the API's error CODE, not its message (DEC-165). The two
        // failures are fixed by different actions — retype a password you already
        // know, versus choose a different new one — so collapsing them would put
        // the user in a guessing loop. The API already supplies safe wording for
        // both; anything unrecognised falls back to its message rather than a
        // hardcoded guess about what went wrong.
        const code = err instanceof ApiError ? err.code : null;
        if (code === 'CURRENT_PASSWORD_INCORRECT') {
          setErrorMessage('That current password is not correct.');
          return;
        }
        if (code === 'NEW_PASSWORD_REJECTED') {
          setErrorMessage(
            'That new password does not meet the requirements. Choose a different one.',
          );
          return;
        }
        setErrorMessage(
          err instanceof Error && err.message !== ''
            ? err.message
            : 'Could not change your password. Try again.',
        );
      },
    },
  );

  const onSubmit: SubmitHandler<ChangePasswordForm> = (values) => {
    setErrorMessage(null);
    setDone(false);
    mutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(onSubmit)(e);
      }}
      aria-label="Change your password"
    >
      <PasswordField
        key={`current-${fieldsGeneration.toString()}`}
        label="Current password"
        id="cp-current-password"
        autoComplete="current-password"
        error={form.formState.errors.currentPassword?.message}
        {...form.register('currentPassword')}
      />
      <PasswordField
        key={`new-${fieldsGeneration.toString()}`}
        label="New password"
        id="cp-new-password"
        autoComplete="new-password"
        hint={PASSWORD_RULES_TEXT}
        error={form.formState.errors.newPassword?.message}
        {...form.register('newPassword')}
      />
      <PasswordField
        key={`confirm-${fieldsGeneration.toString()}`}
        label="Confirm new password"
        id="cp-confirm-password"
        autoComplete="new-password"
        error={form.formState.errors.confirmNewPassword?.message}
        {...form.register('confirmNewPassword')}
      />

      <button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Change password'}
      </button>

      {done && <p role="status">Your password has been changed.</p>}
      {errorMessage !== null && <p role="alert">{errorMessage}</p>}
    </form>
  );
};
